/**
 * Reading a book's `generation` (rest-api automation/horoscopeContent): which
 * state it is in, what staff can do, and the review warnings by section.
 */

/** Statuses where a job is still to be requested or still running at gravur. */
export const RUNNING_STATUSES = ['pending', 'queued', 'processing']

export function generationStatus(book) {
  return (book.generation && book.generation.status) || 'none'
}

export function isGenerating(book) {
  return RUNNING_STATUSES.includes(generationStatus(book))
}

/** The staff upload wins over any generated text. */
export function hasStaffUpload(book) {
  return !!(book.content && book.content.source === 'upload')
}

/**
 * A generated report attached and the background continuation (assemble, maybe
 * submit) not done yet: nothing assembled, no error, not marked as carried through.
 */
export function isContinuing(book) {
  const generation = book.generation
  if (!generation || !book.content || book.content.source !== 'gravur') return false
  if (generation.continuedAt || book.lastError) return false
  const assemblies = book.assemblies || {}
  return !assemblies.print && !assemblies.digital
}

/* Book type: what the book is about (rest-api `bookType`), apart from its design */

/** Names of the book types, as `$t` keys. */
export const BOOK_TYPE_NAMES = {
  persoenlichkeit: 'Personality',
  kinder: 'Children',
  beruf: 'Career',
  partner: 'Partner',
}

export function bookTypeName(type) {
  return BOOK_TYPE_NAMES[type] || type || ''
}

/** gravur's report definitions by book type (rest-api CONTENT_DEFINITIONS); partner comes later. */
export const TYPE_DEFINITIONS = {
  persoenlichkeit: 'natal-personality',
  kinder: 'natal-child',
  beruf: 'natal-career',
}

/** Book types staff can generate, in menu order; the rest are shown disabled. */
export const GENERATED_TYPES = ['persoenlichkeit', 'kinder', 'beruf']

/** Classic designs are bound to one type (rest-api `bookTypeOf`): their type cannot be switched. */
export const CLASSIC_DESIGN_TYPES = {
  PERSONALITY: 'persoenlichkeit',
  PROFESSION: 'beruf',
  CHILDREN: 'kinder',
  PARTNERS: 'partner',
}

const typeOfDefinition = (definition) =>
  Object.keys(TYPE_DEFINITIONS).find((type) => TYPE_DEFINITIONS[type] === definition) || null

/** The type the attached generated text was written as, when known. */
export function textBookType(book) {
  const content = book.content
  if (!content || content.source !== 'gravur') return null
  if (content.bookType) return content.bookType
  if (content.definition) return typeOfDefinition(content.definition)
  const generation = book.generation
  return generation && generation.jobId === content.jobId
    ? generation.bookType || typeOfDefinition(generation.definition)
    : null
}

/**
 * Staff switched the book's type after its job was requested: the next
 * Generate asks gravur for a new text of the book's type.
 */
export function isOtherType(book) {
  const generation = book.generation
  if (hasStaffUpload(book) || !book.bookType || !generation || !generation.definition) return false
  const wanted = TYPE_DEFINITIONS[book.bookType]
  return !!wanted && generation.definition !== wanted
}

/** Partner books carry a second subject: the partners design, or the partner type in another design. */
export function isPartnerBook(book) {
  return book.variant === 'PARTNERS' || book.bookType === 'partner'
}

/* Language: the book's (`book.language`, the store's unless staff switched it) vs. its text's */

/** Names of the languages a book can be made in, as `$t` keys. */
export const LANGUAGE_NAMES = { de: 'German', en: 'English' }

export function languageName(code) {
  return LANGUAGE_NAMES[code] || (code ? code.toUpperCase() : '')
}

/** The language the attached generated text is written in, when known (older records do not say). */
export function textLanguage(book) {
  const content = book.content
  if (!content || content.source !== 'gravur') return null
  if (content.locale) return content.locale
  const generation = book.generation
  return generation && generation.jobId === content.jobId ? generation.locale || null : null
}

/**
 * Staff switched the book's language after its job was requested: the next
 * Generate asks gravur for a new text in the book's language.
 */
export function isOtherLanguage(book) {
  const generation = book.generation
  if (hasStaffUpload(book) || !book.language) return false
  return !!(generation && generation.locale && generation.locale !== book.language)
}

/* Gender: each subject's (`book.genders`, rest-api `bookGenders`) vs. the one its text was written with */

/** The genders a subject can have (rest-api BOOK_GENDERS), in menu order. */
export const GENDERS = ['female', 'male', 'unknown']

/** Names of the genders, as `$t` keys. */
export const GENDER_NAMES = { female: 'Female', male: 'Male', unknown: 'Gender unknown' }

export function genderName(gender) {
  return GENDER_NAMES[gender] || GENDER_NAMES.unknown
}

const knownGender = (gender) => (GENDERS.includes(gender) ? gender : 'unknown')

/** Each subject's gender as the book is made: `{ native, partner? }`, `unknown` where the data has none. */
export function bookGenders(book) {
  if (book.genders && book.genders.native) return book.genders
  const p = book.properties || {}
  return {
    native: knownGender(p.gender),
    ...(isPartnerBook(book) && { partner: knownGender(p.genderPartner) }),
  }
}

/**
 * The genders the attached generated text was written with, `{ native, partner? }`,
 * or null when not recorded (texts written before genders were, and uploads).
 */
export function textGenders(book) {
  const content = book.content
  if (!content || content.source !== 'gravur') return null
  const generation = book.generation
  const written = content.gender
    ? content
    : generation && generation.jobId === content.jobId && generation.gender
      ? generation
      : null
  if (!written) return null
  return { native: written.gender, ...(written.genderPartner && { partner: written.genderPartner }) }
}

/**
 * The first subject whose recorded gender differs from the book's now:
 * `{ subject, written, book }`, or null. An unrecorded gender counts as
 * matching — rest-api assembles those texts (`genderMismatch`).
 */
function mismatch(written, book) {
  if (!written) return null
  const genders = bookGenders(book)
  if (written.native && written.native !== genders.native) {
    return { subject: 'native', written: written.native, book: genders.native }
  }
  if (written.partner && genders.partner && written.partner !== genders.partner) {
    return { subject: 'partner', written: written.partner, book: genders.partner }
  }
  return null
}

/** The attached text was written with another gender than the book's now (assembly refuses it). */
export function textGenderMismatch(book) {
  return mismatch(textGenders(book), book)
}

/**
 * Staff switched a subject's gender after its job was requested: the next
 * Generate asks gravur for a new text with the book's genders.
 */
export function isOtherGender(book) {
  const generation = book.generation
  if (hasStaffUpload(book) || !generation || !generation.gender) return false
  return !!mismatch({ native: generation.gender, partner: generation.genderPartner }, book)
}

/** Whether the dialog should keep refreshing this book. */
export function needsPolling(book) {
  return isGenerating(book) || isContinuing(book)
}

export const STATUS_PRESENTATION = {
  none: { label: 'Not generated', color: 'grey', icon: 'mdi-text-box-outline' },
  pending: { label: 'Waiting for gravur', color: 'blue-grey', icon: 'mdi-timer-sand' },
  queued: { label: 'Queued', color: 'info', icon: 'mdi-tray-full' },
  processing: { label: 'Writing', color: 'info', icon: 'mdi-progress-pencil' },
  completed: { label: 'Generated', color: 'success', icon: 'mdi-check-circle-outline' },
  needs_review: { label: 'Needs review', color: 'warning', icon: 'mdi-alert-outline' },
  failed: { label: 'Failed', color: 'error', icon: 'mdi-alert-circle-outline' },
  cancelled: { label: 'Cancelled', color: 'grey darken-1', icon: 'mdi-cancel' },
  unsupported: { label: 'Not automated', color: 'grey darken-1', icon: 'mdi-hand-back-right-outline' },
}

export function statusPresentation(book) {
  return STATUS_PRESENTATION[generationStatus(book)] || STATUS_PRESENTATION.none
}

/* Section names as the book prints them (gravur's section ids are English slugs). */

const PLANETS = {
  sun: 'Sonne',
  moon: 'Mond',
  mercury: 'Merkur',
  venus: 'Venus',
  mars: 'Mars',
  jupiter: 'Jupiter',
  saturn: 'Saturn',
  uranus: 'Uranus',
  neptune: 'Neptun',
  pluto: 'Pluto',
  chiron: 'Chiron',
  lilith: 'Lilith',
  'north-node': 'Mondknoten',
  'true-node': 'Mondknoten',
  ascendant: 'Aszendent',
  mc: 'MC',
  midheaven: 'Medium Coeli',
}

const ASPECTS = {
  conjunction: 'Konjunktion',
  opposition: 'Opposition',
  square: 'Quadrat',
  trine: 'Trigon',
  sextile: 'Sextil',
  quincunx: 'Quincunx',
  'semi-sextile': 'Halbsextil',
  semisextile: 'Halbsextil',
}

const SIGNS = {
  aries: 'Widder',
  taurus: 'Stier',
  gemini: 'Zwillinge',
  cancer: 'Krebs',
  leo: 'Löwe',
  virgo: 'Jungfrau',
  libra: 'Waage',
  scorpio: 'Skorpion',
  sagittarius: 'Schütze',
  capricorn: 'Steinbock',
  aquarius: 'Wassermann',
  pisces: 'Fische',
}

const FIXED_SECTIONS = {
  foreword: 'Vorwort',
  'psychological-type': 'Psychologischer Typ',
  synthesis: 'Synthese',
  epilogue: 'Nachwort',
  ascendant: 'Aszendent',
  retrograde: 'Rückläufige Planeten',
}

/** Longest planet slug first, so `north-node-…` is not read as `north`. */
const PLANET_KEYS = Object.keys(PLANETS).sort((a, b) => b.length - a.length)

/** `planet-venus` → "Venus", `aspect-mercury-neptune-square` → "Merkur Quadrat Neptun"; unknown ids as they are. */
export function sectionLabel(sectionId) {
  if (!sectionId) return null
  if (FIXED_SECTIONS[sectionId]) return FIXED_SECTIONS[sectionId]

  const planet = /^planet-(.+)$/.exec(sectionId)
  if (planet && PLANETS[planet[1]]) return PLANETS[planet[1]]

  const stellium = /^stellium-(house|sign)-(.+)$/.exec(sectionId)
  if (stellium) {
    if (stellium[1] === 'house') return `Stellium im ${stellium[2]}. Haus`
    const sign = SIGNS[stellium[2].toLowerCase()]
    if (sign) return `Stellium in ${sign}`
  }

  const aspect = /^aspect-(.+)$/.exec(sectionId)
  if (aspect) {
    let rest = aspect[1]
    const names = []
    for (let i = 0; i < 2; i++) {
      const key = PLANET_KEYS.find((k) => rest === k || rest.startsWith(`${k}-`))
      if (!key) return sectionId
      names.push(PLANETS[key])
      rest = rest.slice(key.length + 1)
    }
    if (!ASPECTS[rest]) return sectionId
    return `${names[0]} ${ASPECTS[rest]} ${names[1]}`
  }

  return sectionId
}

/**
 * Warnings (`kind@sectionId`, or `kind` for the whole report) grouped by
 * section in the order they first appear; each kind once, with its count.
 */
export function groupWarnings(warnings) {
  const groups = []
  const bySection = {}
  for (const warning of warnings || []) {
    const at = warning.indexOf('@')
    const kind = at === -1 ? warning : warning.slice(0, at)
    const sectionId = at === -1 ? null : warning.slice(at + 1) || null
    const key = sectionId || ''
    if (!bySection[key]) {
      bySection[key] = { sectionId, label: sectionLabel(sectionId), kinds: [] }
      groups.push(bySection[key])
    }
    const existing = bySection[key].kinds.find((k) => k.kind === kind)
    if (existing) existing.count++
    else bySection[key].kinds.push({ kind, count: 1 })
  }
  // Report-wide findings last: they have no section to regenerate.
  return [...groups.filter((g) => g.sectionId), ...groups.filter((g) => !g.sectionId)]
}

/** gravur's finding kinds, as staff read them (keys of the locale files). */
export const WARNING_LABELS = {
  'language-leak': 'Mixed language',
  'banned-term': 'Banned term',
  'banned-heading': 'Banned heading',
  'invented-aspect': 'Invented aspect',
  'wrong-aspect-type': 'Wrong aspect type',
  'missing-aspect': 'Missing aspect',
  'time-dependent': 'Depends on the birth time',
  'moon-sign': 'Moon sign uncertain',
  'address-form': 'Form of address',
  truncated: 'Text cut off',
  'too-short': 'Too short',
  structure: 'Structure',
  'missing-marker': 'Missing marker',
  duplication: 'Duplicated text',
  repetition: 'Repetition',
  'overused-word': 'Overused word',
  temperament: 'Temperament',
  style: 'Style',
  'model-error': 'Model error',
  spelling: 'Spelling',
  pronoun: 'Pronoun / gender',
  'subject-name': 'Name spelling',
  placeholder: 'Leftover placeholder or marker',
  'doubled-word': 'Doubled word',
  'unbalanced-punctuation': 'Unbalanced quotes or brackets',
  'restated-ending': 'Closing paragraph restates the chapter',
  'unknown-word': 'Unknown word (check by eye)',
}

export function warningLabel(kind) {
  return WARNING_LABELS[kind] || kind
}
